/**
 * Shared Analytics Event Catalog
 *
 * Centralized event naming for consistent analytics tracking across client and server.
 * Import this in both apps/web and apps/api for type-safe event tracking.
 */
/**
 * Authentication events (shared between client and server)
 */
export const AuthAnalyticsEvents = {
    USER_REGISTERED: 'user_registered',
    USER_LOGGED_IN: 'user_logged_in',
    USER_LOGGED_OUT: 'user_logged_out',
    USER_LOGIN_SUCCESS: 'user_login_success',
    USER_LOGIN_FAILED: 'user_login_failed',
    PASSWORD_RESET_REQUESTED: 'password_reset_requested',
    PASSWORD_RESET_COMPLETED: 'password_reset_completed',
    EMAIL_VERIFIED: 'email_verified',
};
/**
 * Onboarding events (primarily client-side)
 */
export const OnboardingAnalyticsEvents = {
    ONBOARDING_STARTED: 'onboarding_started',
    ONBOARDING_COMPLETED: 'onboarding_completed',
    ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
};
/**
 * Repository events (shared)
 */
export const RepositoryAnalyticsEvents = {
    REPOSITORY_CONNECTED: 'repository_connected',
    REPOSITORY_DELETED: 'repository_deleted',
    ANALYSIS_STARTED: 'analysis_started',
    ANALYSIS_COMPLETED: 'analysis_completed',
    ANALYSIS_FAILED: 'analysis_failed',
};
/**
 * PRD Generation events (shared)
 */
export const PRDAnalyticsEvents = {
    PRD_CREATED: 'prd_created',
    PRD_GENERATION_STARTED: 'prd_generation_started',
    PRD_GENERATION_COMPLETED: 'prd_generation_completed',
    PRD_GENERATION_FAILED: 'prd_generation_failed',
    PRD_EXPORTED: 'prd_exported',
    PRD_SHARED: 'prd_shared',
};
/**
 * ACF (AI Code Forge) session events (shared)
 */
export const ACFAnalyticsEvents = {
    ACF_SESSION_CREATED: 'acf_session_created',
    ACF_SESSION_COMPLETED: 'acf_session_completed',
    ACF_CODE_GENERATED: 'acf_code_generated',
    ACF_CODE_EXECUTED: 'acf_code_executed',
};
/**
 * Billing/Subscription events (shared)
 */
export const BillingAnalyticsEvents = {
    SUBSCRIPTION_CREATED: 'subscription_created',
    SUBSCRIPTION_UPDATED: 'subscription_updated',
    SUBSCRIPTION_CANCELLED: 'subscription_cancelled',
    SUBSCRIPTION_UPGRADED: 'subscription_upgraded',
    SUBSCRIPTION_DOWNGRADED: 'subscription_downgraded',
    PAYMENT_SUCCEEDED: 'payment_succeeded',
    PAYMENT_FAILED: 'payment_failed',
};
/**
 * Workspace events (shared)
 */
export const WorkspaceAnalyticsEvents = {
    WORKSPACE_CREATED: 'workspace_created',
    WORKSPACE_MEMBER_INVITED: 'workspace_member_invited',
    WORKSPACE_MEMBER_JOINED: 'workspace_member_joined',
    WORKSPACE_MEMBER_ADDED: 'workspace_member_added',
    WORKSPACE_MEMBER_REMOVED: 'workspace_member_removed',
};
/**
 * API/Server-only events
 */
export const APIAnalyticsEvents = {
    API_REQUEST: 'api_request',
    API_ERROR: 'api_error',
    RATE_LIMIT_HIT: 'rate_limit_hit',
    WEBHOOK_RECEIVED: 'webhook_received',
    WEBHOOK_DELIVERED: 'webhook_delivered',
    WEBHOOK_FAILED: 'webhook_failed',
};
/**
 * Usage tracking events (primarily server-side)
 */
export const UsageAnalyticsEvents = {
    LLM_TOKENS_USED: 'llm_tokens_used',
    USAGE_LIMIT_WARNING: 'usage_limit_warning',
    USAGE_LIMIT_EXCEEDED: 'usage_limit_exceeded',
};
/**
 * Security events (shared)
 */
export const SecurityAnalyticsEvents = {
    SECURITY_SCAN_COMPLETED: 'security_scan_completed',
    VULNERABILITY_FOUND: 'vulnerability_found',
};
/**
 * General feature/error events (shared)
 */
export const GeneralAnalyticsEvents = {
    FEATURE_USED: 'feature_used',
    ERROR_OCCURRED: 'error_occurred',
    FEEDBACK_SUBMITTED: 'feedback_submitted',
};
/**
 * Combined analytics event catalog for client-side usage
 * Maps to all events that can be tracked from the browser
 */
export const ClientAnalyticsEvents = {
    ...AuthAnalyticsEvents,
    ...OnboardingAnalyticsEvents,
    ...RepositoryAnalyticsEvents,
    ...PRDAnalyticsEvents,
    ...ACFAnalyticsEvents,
    ...BillingAnalyticsEvents,
    ...WorkspaceAnalyticsEvents,
    ...SecurityAnalyticsEvents,
    ...GeneralAnalyticsEvents,
};
/**
 * Combined analytics event catalog for server-side usage
 * Maps to all events that can be tracked from the backend
 */
export const ServerAnalyticsEvents = {
    ...AuthAnalyticsEvents,
    ...RepositoryAnalyticsEvents,
    ...PRDAnalyticsEvents,
    ...ACFAnalyticsEvents,
    ...BillingAnalyticsEvents,
    ...WorkspaceAnalyticsEvents,
    ...APIAnalyticsEvents,
    ...UsageAnalyticsEvents,
    ...SecurityAnalyticsEvents,
    ...GeneralAnalyticsEvents,
};
//# sourceMappingURL=analytics.js.map